import { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { Menu, X, ChevronDown, ChevronRight, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { trackPhoneClick, trackWhatsAppClick } from "@/lib/analytics";

const services = [
  // Bulk SMS Services
  { name: "Bulk SMS", href: "/bulk-sms", category: "Bulk SMS" },
  { name: "OTP SMS", href: "/otp-sms", category: "Bulk SMS" },
  { name: "Promotional SMS", href: "/promotional-sms", category: "Bulk SMS" },
  { name: "Transactional SMS", href: "/transactional-sms", category: "Bulk SMS" },
  { name: "International Bulk SMS", href: "/international-bulk-sms", category: "Bulk SMS" },
  { name: "SMS Reseller Program", href: "/reseller-program", category: "Bulk SMS" },

  // Messaging Services
  { name: "WhatsApp Business API", href: "/whatsapp-business-api", category: "Messaging" },
  { name: "RCS Messaging", href: "/rcs-messaging", category: "Messaging" },
  { name: "Email Marketing", href: "/email-marketing", category: "Messaging" },

  // Voice Services
  { name: "Voice Call Service", href: "/voice-call", category: "Voice Services" },
  { name: "Toll-Free Number", href: "/services/toll-free-number", category: "Voice Services" },
  { name: "IVR Solutions", href: "/services/ivr-solutions", category: "Voice Services" },
  { name: "Missed Call Service", href: "/services/missed-call-services", category: "Voice Services" },

  // Digital Marketing
  { name: "Google Ads", href: "/google-ads-services", category: "Digital Marketing" },
  { name: "Meta Ads", href: "/meta-ads", category: "Digital Marketing" },
  { name: "YouTube Ads", href: "/services/youtube-ads", category: "Digital Marketing" },
  { name: "SEO Services", href: "/seo-services", category: "Digital Marketing" },
  { name: "Social Media Marketing", href: "/social-media-marketing", category: "Digital Marketing" },
  { name: "Social Media Monetization", href: "/social-media-monetization", category: "Digital Marketing" },

  // Web & Design
  { name: "Website Development", href: "/website-design", category: "Web & Design" },

  // Compliance
  { name: "DLT Registration", href: "/dlt-registration", category: "Compliance" },

  // View All
  { name: "All Services", href: "/services", category: "View All" },
];

const tools = [
  { name: "AI Template Generator", href: "/tools/template-generator" },
  { name: "DLT Template Validator", href: "/tools/dlt-validator" },
  { name: "WhatsApp Number Scrubber", href: "/tools/whatsapp-number-scrubber" },
  { name: "SMS Character Counter", href: "/tools/sms-character-counter" },
  { name: "Free SEO Checker", href: "/tools/seo-checker" },
  { name: "SMS API Documentation", href: "/api-docs" },
  { name: "DLT Compliance Guide", href: "/dlt-guide" },
  { name: "SMS Statistics India 2026", href: "/sms-marketing-statistics-india" },
];

const navLinks = [
  { name: "Services", href: "/services", dropdown: services },
  { name: "Industries", href: "/industries" },
  { name: "Tools", href: "/tools", dropdown: [{ name: "All Tools", href: "/tools" }, ...tools] },
  { name: "Pricing", href: "/pricing" },
  { name: "Blog", href: "/blog" },
  { name: "Resources", href: "/resources" },
  { name: "Contact", href: "/contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  const [mobileExpandedCategory, setMobileExpandedCategory] = useState<string | null>(null);
  const [mobileLocationsOpen, setMobileLocationsOpen] = useState(false);
  const [mobileToolsOpen, setMobileToolsOpen] = useState(false);
  const [location] = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setOpenDropdown(null);
    setExpandedCategory(null);
    setMobileLocationsOpen(false);
    setMobileExpandedCategory(null);
    setMobileToolsOpen(false);
  }, [location]);

  // Lock body scroll when mobile menu is open (prevents iOS background scroll)
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    } else {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      // Reset all mobile menu states when menu closes
      setMobileExpandedCategory(null);
      setMobileToolsOpen(false);
      setMobileLocationsOpen(false);
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    };
  }, [mobileOpen]);

  // Get unique categories for Services dropdown
  const getCategories = (items: any[]) => {
    return Array.from(new Set(items.map(item => item.category)));
  };

  return (
    <>
      {/* Main Navigation */}
      <header
        className={`sticky top-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-white/95 backdrop-blur-xl shadow-lg shadow-navy/5"
            : "bg-[#0a0e1a]/95 backdrop-blur-md"
        }`}
      >
        <div className="container flex items-center justify-between h-20 gap-4">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-3 group shrink-0">
            <div className="relative">
              <img
                src="/assets/bharat_bulk_sms_logo_65px_4a34d862_c8404b73.webp"
                alt="Bharat Bulk SMS"
                className="h-10 w-auto max-w-[140px] object-contain relative z-10 transition-all duration-300 group-hover:scale-105 group-hover:brightness-110"
                width={200}
                height={48}
                loading="eager"
                style={{
                  filter: 'drop-shadow(0 4px 6px rgba(255,107,44,0.3)) drop-shadow(0 1px 3px rgba(0,0,0,0.2))',
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-r from-saffron/10 via-transparent to-orange-500/10 rounded-lg blur-md opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-0" />
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <div key={link.name} className="relative group" onMouseLeave={() => setOpenDropdown(null)}>
                {link.dropdown ? (
                  <button
                    type="button"
                    onClick={() => setOpenDropdown(openDropdown === link.name ? null : link.name)}
                    onKeyDown={(event) => {
                      if (event.key === "Escape") setOpenDropdown(null);
                    }}
                    aria-haspopup="menu"
                    aria-expanded={openDropdown === link.name}
                    aria-controls={`desktop-menu-${link.name.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`px-3 py-2 text-sm font-medium rounded-md transition-colors flex items-center gap-1 ${
                      link.dropdown.some((s: any) => location === s.href) || location.startsWith(link.href.split('/').slice(0, -1).join('/') || '/never')
                        ? "text-saffron"
                        : scrolled ? "text-foreground hover:text-saffron" : "text-white/90 hover:text-saffron"
                    }`}
                  >
                    {(link as any).isLocations && <MapPin className="w-3.5 h-3.5" />}
                    {link.name}
                    <ChevronDown className="w-3.5 h-3.5 transition-transform duration-200 group-hover:rotate-180" />
                  </button>
                ) : (
                  <Link
                    href={link.href}
                    className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      location === link.href
                        ? "text-saffron"
                        : scrolled ? "text-foreground hover:text-saffron" : "text-white/90 hover:text-saffron"
                    }`}
                  >
                    {link.name}
                  </Link>
                )}
                {link.dropdown && (
                  <div
                    id={`desktop-menu-${link.name.toLowerCase().replace(/\s+/g, "-")}`}
                    role="menu"
                    onKeyDown={(event) => {
                      if (event.key === "Escape") setOpenDropdown(null);
                    }}
                    className={`absolute top-full left-0 pt-2 transition-all duration-200 z-50 ${
                      openDropdown === link.name ? "opacity-100 visible" : "opacity-0 invisible group-hover:opacity-100 group-hover:visible"
                    }`}
                  >
                    <div className={`bg-white rounded-xl shadow-xl shadow-navy/10 border border-border p-3 max-h-[600px] overflow-y-auto ${
                      (link as any).isLocations || link.name === "Services" ? "min-w-[520px]" : "min-w-[280px]"
                    }`}>

                      {/* Locations: 2-column grid with city cards */}
                      {(link as any).isLocations ? (
                        <div>
                          <div className="flex items-center gap-2 px-3 pb-3 mb-2 border-b border-border">
                            <MapPin className="w-4 h-4 text-saffron" />
                            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Bulk SMS Service by City</span>
                          </div>
                          <div className="grid grid-cols-2 gap-1.5">
                            {(link.dropdown as any[]).map((item) => (
                              <Link
                                key={item.href}
                                href={item.href}
                                role="menuitem"
                                className="group/city flex items-start gap-2.5 px-3 py-2.5 rounded-lg hover:bg-orange-50 transition-colors min-h-[44px]"
                              >
                                <MapPin className="w-3.5 h-3.5 text-saffron/60 group-hover/city:text-saffron mt-0.5 shrink-0 transition-colors" />
                                <div>
                                  <div className="text-sm font-semibold text-foreground group-hover/city:text-saffron transition-colors leading-tight">{item.name}</div>
                                  <div className="text-[11px] text-muted-foreground leading-tight mt-0.5">{item.tagline}</div>
                                </div>
                              </Link>
                            ))}
                          </div>
                          <div className="mt-2 pt-2 border-t border-border">
                            <Link
                              href="/industries"
                              className="flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-saffron hover:bg-orange-50 rounded-lg transition-colors"
                            >
                              View All Industries & Locations
                              <ChevronRight className="w-3.5 h-3.5" />
                            </Link>
                          </div>
                        </div>
                      ) : link.name === "Services" ? (
                        /* Services: multi-column grid like Locations */
                        <div>
                          <div className="flex items-center gap-2 px-3 pb-3 mb-2 border-b border-border">
                            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Our Services</span>
                          </div>
                          <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
                            {getCategories(link.dropdown as any).filter(c => c !== "View All").map((category) => {
                              const categoryServices = (link.dropdown as any).filter((item: any) => item.category === category);
                              return (
                                <div key={category} className="mb-3">
                                  <div className="px-3 py-1.5 text-[11px] font-bold text-saffron uppercase tracking-wider">{category}</div>
                                  {categoryServices.map((item: any) => (
                                    <Link
                                      key={item.href}
                                      href={item.href}
                                      role="menuitem"
                                      className="block px-3 py-2 text-sm rounded-lg hover:bg-orange-50 text-foreground hover:text-saffron transition-colors leading-tight"
                                    >
                                      {item.name}
                                    </Link>
                                  ))}
                                </div>
                              );
                            })}
                          </div>
                          <div className="mt-2 pt-2 border-t border-border">
                            <Link
                              href="/services"
                              role="menuitem"
                              className="flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-saffron hover:bg-orange-50 rounded-lg transition-colors"
                            >
                              View All Services
                              <ChevronRight className="w-3.5 h-3.5" />
                            </Link>
                          </div>
                        </div>
                      ) : (
                        /* Tools & other dropdowns: clean flat list */
                        <div>
                          {link.dropdown.map((item: any) => (
                            <Link
                              key={item.href}
                              href={item.href}
                              role="menuitem"
                              className="block px-4 py-2.5 text-sm rounded-lg hover:bg-orange-50 text-foreground hover:text-saffron transition-colors"
                            >
                              {item.name}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </nav>

          {/* CTA + Mobile Toggle */}
          <div className="flex items-center gap-3">
            <Button
              asChild
              variant="outline"
              className="hidden sm:inline-flex border-saffron/40 bg-transparent text-saffron hover:bg-saffron/10 hover:text-saffron font-semibold transition-all duration-200 active:scale-[0.97]"
            >
              <a href="https://app.bharatbulksms.com/" target="_blank" rel="noopener noreferrer">
                APP Login
              </a>
            </Button>
            <Button
              asChild
              className="hidden sm:inline-flex bg-saffron hover:bg-saffron-light text-white font-semibold shadow-lg shadow-saffron/25 transition-all duration-200 active:scale-[0.97]"
            >
              <a href="https://sms.gooadvert.com/Account/Login" target="_blank" rel="noopener noreferrer">
                Start Sending
              </a>
            </Button>
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className={`lg:hidden p-2.5 rounded-lg transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center ${scrolled ? 'hover:bg-muted text-foreground' : 'hover:bg-white/10 text-white'}`}
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div
            className="lg:hidden border-t border-border bg-white animate-in slide-in-from-top-2 duration-200"
            style={{
              maxHeight: 'calc(100dvh - 5rem)',
              overflowY: 'auto',
              WebkitOverflowScrolling: 'touch',
              overscrollBehavior: 'contain',
            }}
          >
            <nav className="container py-4 space-y-1">
              {navLinks.map((link) => (
                <div key={link.name}>
                  {link.dropdown ? null : (
                    <Link
                      href={link.href}
                      className={`block px-4 py-3 rounded-lg text-base font-medium transition-colors ${
                        location === link.href
                          ? "bg-cream text-saffron"
                          : "text-foreground hover:bg-cream"
                      }`}
                    >
                      {link.name}
                    </Link>
                  )}

                  {/* Services dropdown */}
                  {link.dropdown && (
                    <div className="pl-2 space-y-1 mt-1">
                      {link.name === "Services" ? (
                        <div className="space-y-1">
                          {getCategories(link.dropdown as any).map((category) => {
                            const categoryServices = (link.dropdown as any).filter((item: any) => item.category === category);
                            const isExpanded = mobileExpandedCategory === category;
                            return (
                              <div key={category}>
                                <button
                                  type="button"
                                  onClick={() => setMobileExpandedCategory(isExpanded ? null : category)}
                                  aria-expanded={isExpanded}
                                  aria-controls={`mobile-service-category-${category.toLowerCase().replace(/\s+/g, "-")}`}
                                  className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg hover:bg-cream text-foreground hover:text-saffron transition-colors font-semibold text-sm min-h-[44px]"
                                >
                                  <span>{category}</span>
                                  <ChevronRight className={`w-4 h-4 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`} />
                                </button>
                                {isExpanded && (
                                  <div id={`mobile-service-category-${category.toLowerCase().replace(/\s+/g, "-")}`} className="pl-4 space-y-0.5 mt-1 border-l-2 border-saffron/30">
                                    {categoryServices.map((item: any) => (
                                      <Link
                                        key={item.href}
                                        href={item.href}
                                        className="block px-3 py-2.5 text-sm rounded-lg hover:bg-cream text-muted-foreground hover:text-saffron transition-colors min-h-[44px] flex items-center"
                                      >
                                        {item.name}
                                      </Link>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ) : link.name === "Tools" ? (
                        <>
                          <button
                            type="button"
                            onClick={() => setMobileToolsOpen(!mobileToolsOpen)}
                            aria-expanded={mobileToolsOpen}
                            aria-controls="mobile-tools-menu"
                            className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg hover:bg-cream text-foreground hover:text-saffron transition-colors font-semibold text-sm min-h-[44px]"
                          >
                            <span>View All Tools</span>
                            <ChevronRight className={`w-4 h-4 transition-transform duration-200 ${mobileToolsOpen ? 'rotate-90' : ''}`} />
                          </button>
                          {mobileToolsOpen && (
                            <div id="mobile-tools-menu" className="pl-4 space-y-0.5 mt-1 border-l-2 border-saffron/30">
                              {link.dropdown.map((item: any) => (
                                <Link
                                  key={item.href}
                                  href={item.href}
                                  className="block px-3 py-2.5 text-sm rounded-lg hover:bg-cream text-muted-foreground hover:text-saffron transition-colors min-h-[44px] flex items-center"
                                >
                                  {item.name}
                                </Link>
                              ))}
                            </div>
                          )}
                        </>
                      ) : (
                        link.dropdown.map((item: any) => (
                          <Link
                            key={item.href}
                            href={item.href}
                            className="block px-4 py-2.5 text-sm text-muted-foreground hover:text-saffron rounded-lg min-h-[44px] flex items-center"
                          >
                            {item.name}
                          </Link>
                        ))
                      )}
                    </div>
                  )}
                </div>
              ))}
              <Link
                href="/about"
                className={`block px-4 py-3 rounded-lg text-base font-medium transition-colors ${
                  location === "/about"
                    ? "bg-cream text-saffron"
                    : "text-foreground hover:bg-cream"
                }`}
              >
                About Us
              </Link>
              <div className="pt-3">
                <Button asChild variant="outline" className="w-full border-saffron/40 bg-transparent text-saffron hover:bg-saffron/10 hover:text-saffron font-semibold mb-2">
                  <a href="https://app.bharatbulksms.com/" target="_blank" rel="noopener noreferrer">
                    APP Login
                  </a>
                </Button>
                <Button asChild className="w-full bg-saffron hover:bg-saffron-light text-white font-semibold">
                  <a href="https://sms.gooadvert.com/Account/Login" target="_blank" rel="noopener noreferrer">
                    Start Sending
                  </a>
                </Button>
              </div>
            </nav>
          </div>
        )}
      </header>
    </>
  );
}
